import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText as GSAPSplitText } from 'gsap/SplitText'
import React, { useEffect, useRef } from 'react'

gsap.registerPlugin(GSAPSplitText, ScrollTrigger)

export interface SplitTextProps {
  text: string
  className?: string
  delay?: number
  duration?: number
  ease?: string | ((t: number) => number)
  splitType?: 'chars' | 'words' | 'lines' | 'words, chars'
  from?: gsap.TweenVars
  to?: gsap.TweenVars
  threshold?: number
  rootMargin?: string
  textAlign?: React.CSSProperties['textAlign']
  onLetterAnimationComplete?: () => void
  children?: React.ReactNode
}

const SplitText: React.FC<SplitTextProps> = ({
  text,
  className = '',
  delay = 100,
  duration = 0.6,
  ease = 'power3.out',
  splitType = 'chars',
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  // threshold = 0.1,
  // rootMargin = '-100px',
  textAlign = 'center',
  onLetterAnimationComplete,
  children,
}) => {
  const ref = useRef<HTMLParagraphElement>(null)
  const animationCompletedRef = useRef(false)
  const hasAnimatedRef = useRef(false)

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      !ref.current ||
      (!text && !children) ||
      hasAnimatedRef.current
    )
      return

    const el = ref.current
    animationCompletedRef.current = false

    const absoluteLines = splitType === 'lines'
    if (absoluteLines) el.style.position = 'relative'

    let splitter: GSAPSplitText
    try {
      splitter = new GSAPSplitText(el, {
        type: splitType,
        absolute: absoluteLines,
        linesClass: 'split-line',
      })
    } catch (error) {
      console.error('Failed to create SplitText:', error)
      return
    }

    let targets: Element[]
    switch (splitType) {
      case 'lines':
        targets = splitter.lines
        break
      case 'words':
        targets = splitter.words
        break
      case 'chars':
        targets = splitter.chars
        break
      default:
        targets = splitter.chars
    }

    if (!targets || targets.length === 0) {
      console.warn('No targets found for SplitText animation')
      splitter.revert()
      return
    }

    targets.forEach((t) => {
      ;(t as HTMLElement).style.willChange = 'transform, opacity'
    })

    // Initial state setzen - Element verstecken
    gsap.set(targets, { ...from, immediateRender: true, force3D: true })

    // Container sichtbar machen sobald Animation startet
    gsap.set(el, { opacity: 1, immediateRender: false })

    const tl = gsap.timeline({
      smoothChildTiming: true,
      onComplete: () => {
        animationCompletedRef.current = true
        hasAnimatedRef.current = true
        gsap.set(targets, {
          ...to,
          clearProps: 'willChange',
          immediateRender: true,
        })
        onLetterAnimationComplete?.()
      },
      scrollTrigger: {
        trigger: el,
        start: `top 85%`,
        toggleActions: 'play none none none',
        once: true,
        markers: false,
      },
    })

    tl.to(targets, {
      ...to,
      duration,
      ease,
      stagger: delay / 1000,
      force3D: true,
    })

    return () => {
      tl.kill()
      ScrollTrigger.getAll().forEach((t) => {
        if (t.trigger === el) t.kill()
      })
      gsap.killTweensOf(targets)
      if (splitter) {
        splitter.revert()
      }
    }
  }, [text]) // Nur bei Text-Änderung neu initialisieren

  return (
    <p
      ref={ref}
      className={`split-parent inline-block overflow-hidden whitespace-normal ${className}`}
      style={{
        textAlign,
        wordWrap: 'break-word',
        opacity: 0, // Initial verstecken
      }}
    >
      {children || text}
    </p>
  )
}

export default SplitText
